/**
 * @file WmsDashboardPage.tsx
 * @description «Обзор склада» (phase19.24 этап 1, шаг 3; phase19.31 шаг О4 —
 * утверждённые макеты (в2) для широкого экрана и (в) для телефона).
 *
 * Экран отвечает на один вопрос: что делать прямо сейчас.
 *  - Заголовок «Обзор склада» в верхней панели, пояснение «<склад> · сегодня,
 *    <дата>»; главное действие одно — «+ Приёмка»; «Новое перемещение» и
 *    «Новое списание» — в «⋯» (SC34). На телефоне и планшете «+ Приёмка» — в
 *    общей нижней полосе каркаса.
 *  - Шесть плиток-ссылок в отфильтрованные реестры.
 *  - «Что нужно сделать» — события, сгруппированные по виду задачи; справа
 *    «Очередь сборки» и «Последние движения»; пустые блоки не показываются.
 *  - Склад выбирается, только если складов больше одного: на широком экране
 *    — список над плитками, на узком — пункт «⋯» со шторкой.
 * Данные — один запрос `GET /admin/wms/dashboard` (`useWmsDashboard`);
 * страница — только раскладка, блоки — в `components/wms/dashboard/*`.
 */

import { ArrowLeftRight, Plus, Trash2, Warehouse } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { useAuth, useHasPermission } from "../../../auth/hooks";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ListError } from "../../../components/ui/ListStates";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { OptionSheet } from "../../../components/ui/OptionSheet";
import type { PageAction } from "../../../components/ui/page-actions";
import { SelectField } from "../../../components/ui/SelectField";
import { DASHBOARD_TEXT, dashboardSubtitle, offlineText } from "../../../components/wms/dashboard/dashboard-texts";
import { useWmsDashboard } from "../../../components/wms/dashboard/useWmsDashboard";
import { WmsDashboardBody } from "../../../components/wms/dashboard/WmsDashboardBody";
import { WmsDashboardCounters } from "../../../components/wms/dashboard/WmsDashboardCounters";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { formatShopTime, getShopTimeZone } from "../../../lib/shop-time";

/** «2 октября» — сегодня по часовому поясу магазина (SC31). */
function todayText(): string {
  return new Date().toLocaleDateString("ru-RU", { day: "numeric", month: "long", timeZone: getShopTimeZone() });
}

export function WmsDashboardPage() {
  const { slug } = useParams<{ slug: string }>();
  const { me } = useAuth();
  // Заводить документы может администратор склада; без права — та же сводка
  // без кнопок создания (правило «права сразу»).
  const canManage = useHasPermission("SHOP_WMS_MANAGE");
  const dashboard = useWmsDashboard();
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const [warehouseSheet, setWarehouseSheet] = useState(false);
  const basePath = `/shop/${slug}/wms`;
  const data = dashboard.data;
  // Заготовки — только пока данных нет: при смене склада экран не схлопывается.
  const firstLoad = dashboard.loading && !data;
  const warehouses = data?.warehouses ?? [];
  const multiWarehouse = warehouses.length > 1;
  const selected = warehouses.find((w) => w.id === dashboard.warehouseId) ?? null;
  const warehouseName = selected?.name ?? (warehouses.length === 1 ? warehouses[0]?.name : undefined) ?? DASHBOARD_TEXT.allWarehouses;
  const warehouseOptions = [
    { value: "", label: DASHBOARD_TEXT.allWarehouses },
    ...warehouses.map((w) => ({ value: w.id, label: w.name })),
  ];

  const menu: PageAction[] = [
    ...(multiWarehouse && compact
      ? [{ key: "warehouse", label: `${DASHBOARD_TEXT.chooseWarehouse}: ${warehouseName}`, icon: Warehouse, run: () => setWarehouseSheet(true) }]
      : []),
    ...(canManage
      ? [
          // Перемещение заводится диалогом на своём реестре — открываем его параметром.
          { key: "transfer", label: DASHBOARD_TEXT.newTransfer, icon: ArrowLeftRight, to: `${basePath}/transfers?create=1` },
          { key: "writeoff", label: DASHBOARD_TEXT.newWriteoff, icon: Trash2, to: `${basePath}/writeoffs/new` },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <CabinetPageHeader
        title={DASHBOARD_TEXT.title}
        subtitle={dashboardSubtitle(warehouseName, todayText())}
        menu={menu}
        menuLabel={DASHBOARD_TEXT.actionsTitle}
        action={
          canManage ? (
            <Button asChild variant="brand">
              <Link to={`${basePath}/receipts/new`} aria-label={DASHBOARD_TEXT.newReceiptLabel}>
                <Plus className="size-4" />
                {DASHBOARD_TEXT.newReceipt}
              </Link>
            </Button>
          ) : undefined
        }
      />

      {multiWarehouse && !compact ? (
        <SelectField
          value={dashboard.warehouseId}
          onChange={dashboard.setWarehouseId}
          options={warehouseOptions}
          placeholder={DASHBOARD_TEXT.allWarehouses}
          ariaLabel={DASHBOARD_TEXT.warehouseLabel}
          className="w-[240px]"
        />
      ) : null}

      {dashboard.offline && data && dashboard.loadedAt ? (
        <NoticeStrip tone="warning">{offlineText(formatShopTime(dashboard.loadedAt.toISOString()))}</NoticeStrip>
      ) : dashboard.error ? (
        <ListError message={DASHBOARD_TEXT.loadError} onRetry={dashboard.reload} retryLabel={DASHBOARD_TEXT.retry} />
      ) : null}

      {/* Без данных из-за ошибки плитки не показываем — одна полоса с «Повторить». */}
      {dashboard.error && !data ? null : (
        <>
          <WmsDashboardCounters
            counters={data?.counters ?? null}
            basePath={basePath}
            warehouseId={dashboard.warehouseId}
            loading={firstLoad}
          />
          <WmsDashboardBody
            data={data}
            loading={firstLoad}
            basePath={basePath}
            slug={slug}
            warehouseId={dashboard.warehouseId}
            warehouseName={selected?.name ?? null}
            currentUserId={me?.user.id ?? null}
            canManage={canManage}
          />
        </>
      )}

      <OptionSheet
        open={warehouseSheet}
        onOpenChange={setWarehouseSheet}
        title={DASHBOARD_TEXT.chooseWarehouse}
        value={dashboard.warehouseId}
        options={warehouseOptions}
        onChange={dashboard.setWarehouseId}
      />
    </div>
  );
}
